import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useListContext } from 'react-admin';
import { Box, Button as MuiButton } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import moment from "moment";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import InputDateField from "../common/InputComponents/InputDateFieldOld";
import {
    DatePresetKey,
    buildDatePresets,
    formatDateMMDDYYYY,
    getDefaultDateRange,
    getPresetFromValues,
    dateStringValidator,
} from '../../utils/dateRangePresets';


const FilterReports = () => {
    const { displayedFilters, filterValues, setFilters } =
        useListContext();
    const defaultRange = useMemo(() => getDefaultDateRange(), []);
    const [activePreset, setActivePreset] = useState<DatePresetKey | null>(null);
    const hasInitializedDefaultRangeRef = useRef(false);

    const presets = useMemo(() => buildDatePresets(), []);

    const validationSchema = Yup.object().shape({
        rangeto: dateStringValidator(),
        rangefrom: dateStringValidator(),
    });

    const form = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            ...filterValues,
        },
    });

    const applyFilter = useCallback((values) => {
        if (Object.keys(values).length > 0) {
          setFilters(values, displayedFilters);
        }
    }, [displayedFilters, setFilters]);

    const applyRangeFilter = useCallback((values: any) => {
        applyFilter({
            ...filterValues,
            ...values,
            app_invitation_type: filterValues?.app_invitation_type || 1,
        });
    }, [applyFilter, filterValues]);

    const applyRange = useCallback((from: Date, to: Date, preset?: DatePresetKey | null) => {
        const formattedFrom = formatDateMMDDYYYY(from);
        const formattedTo = formatDateMMDDYYYY(to);

        form.setValue('rangefrom', formattedFrom, { shouldDirty: true, shouldValidate: true });
        form.setValue('rangeto', formattedTo, { shouldDirty: true, shouldValidate: true });

        setActivePreset(preset ?? getPresetFromValues(presets, formattedFrom, formattedTo));

        applyRangeFilter({
            rangefrom: formattedFrom,
            rangeto: formattedTo,
        });
    }, [applyRangeFilter, form, presets]);

    useLayoutEffect(() => {
        const hasRange = filterValues?.rangefrom && filterValues?.rangeto;

        if (!hasRange) {
            if (!hasInitializedDefaultRangeRef.current) {
                hasInitializedDefaultRangeRef.current = true;
                applyRange(defaultRange.from, defaultRange.to, 'last30');
            }
            return;
        }

        if (!hasInitializedDefaultRangeRef.current) {
            hasInitializedDefaultRangeRef.current = true;
        }

        setActivePreset(getPresetFromValues(presets, filterValues?.rangefrom, filterValues?.rangeto));
    }, [applyRange, defaultRange, filterValues, presets]);

    const handlePresetSelection = useCallback((presetKey: DatePresetKey) => {
        const preset = presets.find((item) => item.key === presetKey);
        if (!preset) {
            return;
        }

        const { from, to } = preset.getRange();
        applyRange(from, to, presetKey);
    }, [applyRange, presets]);

    const matchPresetFromValues = useCallback((fromValue?: string, toValue?: string): (DatePresetKey | null) => {
        return getPresetFromValues(presets, fromValue, toValue);
    }, [presets]);

    const onSubmit = (values: any) => {
        setActivePreset(matchPresetFromValues(values?.rangefrom, values?.rangeto));
        applyRangeFilter(values);
    }

    const start_date = form.watch("rangefrom");
    const end_date = form.watch("rangeto");

    useLayoutEffect(() => setActivePreset(matchPresetFromValues(start_date, end_date)), [end_date, matchPresetFromValues, start_date]);

    return <div>
        <div className='row' style={{ top: 10, zIndex: '99999' }}>

            <div className='col-12 col-md-12 col-sm-12' style={{ zIndex: 1000, position: "relative" }}>
                <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 1,
                                marginBottom: 1,
                            }}
                        >
                            {presets.map(preset => <MuiButton
                                    key={preset.key}
                                    variant={activePreset === preset.key ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => handlePresetSelection(preset.key)}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '12px',
                                        fontWeight: activePreset === preset.key ? 600 : 400,
                                        paddingX: 1.5,
                                        paddingY: 0.5,
                                    }}
                                >
                                    {preset.label}
                                </MuiButton>)}
                        </Box>
                        <Box
                            className="date-label"
                            sx={{
                                     position: "relative",
                                     paddingTop: 1,
                                     display: "flex",
                                     alignItems: "center",
                                     justifyContent: "flex-start",
                                     gap: 1, // adjust to taste (e.g., 0.5–1.5)
                                     "& .emp-inputs": { flex: "0 0 auto" }, // keep each date field from stretching
                            }}
                        >
                            <span style={{ paddingRight: 2, paddingBottom: 10 }}>
                                <strong>From:</strong>
                            </span>
                            <InputDateField
                                source={"rangefrom"}
                                maxDate={new Date()}
                                required={false}
                                width="12ch"
                            />
                            <strong
                                style={{
                                    paddingRight: 2,
                                    paddingLeft: 2,
                                    paddingBottom: 10,
                                    fontSize: "14px",
                                    color: "#303030",
                                    fontWeight: "inherit",
                                }}
                            >
                                &nbsp;To:&nbsp;
                            </strong>
                            <InputDateField
                                source={"rangeto"}
                                maxDate={new Date()}
                                minDate={start_date ? moment(start_date).toDate() : undefined}
                                required={false}
                                width="12ch"
                            />
                            <MuiButton sx={{ mx: 1, marginBottom: 2 }} variant="contained" type="submit">
                                Apply
                            </MuiButton>

                        </Box>

                    </form>
                </FormProvider></div>
        </div>
    </div>
}

export default FilterReports;
